import { ActionDetailsWrapper } from "@app/components/actions/ActionDetailsWrapper";
import type { ToolExecutionDetailsProps } from "@app/components/actions/mcp/details/types";
import { isTextContent } from "@app/lib/actions/mcp_internal_actions/output_schemas";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import { Globe01 } from "@dust-tt/sparkle";
import i18n from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type EgressStatus = "added" | "already_allowed" | "unknown";

function parseStatus(rawText: string | null): EgressStatus {
  if (!rawText) {
    return "unknown";
  }
  if (/^Allowed:/m.test(rawText)) {
    return "added";
  }
  if (/^Already allowed:/m.test(rawText)) {
    return "already_allowed";
  }
  return "unknown";
}

export function MCPSandboxAddEgressDomainDetails({
  displayContext,
  toolParams,
  toolOutput,
}: ToolExecutionDetailsProps) {
  const { t } = useTranslation();
  const domain =
    typeof toolParams.domain === "string" ? toolParams.domain : null;
  const reason =
    typeof toolParams.reason === "string" ? toolParams.reason : null;

  const rawOutputText = useMemo(() => {
    if (!toolOutput) {
      return null;
    }
    const textBlocks = toolOutput.filter(isTextContent);
    return textBlocks.map((b) => b.text).join("\n") || null;
  }, [toolOutput]);

  const isRunning = toolOutput === null;
  const status = useMemo(() => parseStatus(rawOutputText), [rawOutputText]);

  const actionName = isRunning
    ? t(
        "assistant.egressRequestingAccessTo",
        "Requesting access to {{domain}}",
        { domain: domain ?? t("assistant.egressDomainFallback", "domain") }
      )
    : t("assistant.egressRequestAccessTo", "Request access to {{domain}}", {
        domain: domain ?? t("assistant.egressDomainFallback", "domain"),
      });

  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={actionName}
      visual={Globe01}
    >
      {displayContext === "conversation" ? (
        <ConversationView
          domain={domain}
          reason={reason}
          status={status}
          isRunning={isRunning}
        />
      ) : (
        <SidebarView
          domain={domain}
          reason={reason}
          status={status}
          isRunning={isRunning}
        />
      )}
    </ActionDetailsWrapper>
  );
}

interface EgressViewProps {
  domain: string | null;
  reason: string | null;
  status: EgressStatus;
  isRunning: boolean;
}

function statusLabel(status: EgressStatus, isRunning: boolean): string {
  if (isRunning) {
    return i18n.t(
      "assistant.egressPendingUserApproval",
      "Pending user approval…"
    );
  }
  switch (status) {
    case "added":
      return i18n.t(
        "assistant.egressAddedToAllowlist",
        "Added to Computer allowlist"
      );
    case "already_allowed":
      return i18n.t("assistant.egressAlreadyAllowed", "Already allowed");
    case "unknown":
      return i18n.t("assistant.egressNotAdded", "Not added");
    default:
      assertNeverAndIgnore(status);
      return i18n.t("assistant.egressNotAdded", "Not added");
  }
}

function ConversationView({
  domain,
  reason,
  status,
  isRunning,
}: EgressViewProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1 pl-6 text-sm">
      {domain && (
        <div>
          <span className="text-muted-foreground">
            {t("assistant.egressDomainLabelColon", "Domain: ")}
          </span>
          <span className="font-mono">{domain}</span>
        </div>
      )}
      {reason && (
        <div>
          <span className="text-muted-foreground">
            {t("assistant.egressReasonLabelColon", "Reason: ")}
          </span>
          <span>{reason}</span>
        </div>
      )}
      <div>
        <span className="text-muted-foreground">
          {t("assistant.egressStatusLabelColon", "Status: ")}
        </span>
        <span>{statusLabel(status, isRunning)}</span>
      </div>
    </div>
  );
}

function SidebarView({ domain, reason, status, isRunning }: EgressViewProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 py-4 pl-6 text-sm">
      <div className="flex flex-col gap-1">
        <span className="font-medium text-foreground">
          {t("assistant.egressDomainLabel", "Domain")}
        </span>
        <span className="font-mono">{domain ?? "—"}</span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-medium text-foreground">
          {t("assistant.egressReasonLabel", "Reason")}
        </span>
        <span>{reason ?? "—"}</span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-medium text-foreground">
          {t("assistant.egressStatusLabel", "Status")}
        </span>
        <span>{statusLabel(status, isRunning)}</span>
      </div>
    </div>
  );
}
